import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import PublicLayout from "@/components/layout/PublicLayout";
import ProtectedRoute from "@/components/common/ProtectedRoute";

// Public pages
import HomePage from "@/pages/public/HomePage2";
import PresidentMessagePage from "@/pages/public/PresidentMessagePage";
import { AboutPage, MissionPage, PrinciplesPage, DocumentsPage, OrganizationPage } from "@/pages/public/CorporatePages";
import ActivityAreaPage from "@/pages/public/ActivityAreaPage";
import ChairmanshipDetailPage from "@/pages/public/ChairmanshipDetailPage";
import PlaceholderPage from "@/pages/public/PlaceholderPage";
import { ProjectsListPage, ProjectDetailPage, SyriaProgramPage } from "@/pages/public/ProjectsPages";
import { MembershipPage, VolunteerPage, PartnershipPage, ContactPage } from "@/pages/public/FormsPages";
import { NewsListPage, NewsDetailPage, AnnouncementsListPage, AnnouncementDetailPage } from "@/pages/public/NewsPages";
import { KvkkPage, PrivacyPage, CookiesPage, TermsPage, NotFoundPage } from "@/pages/public/LegalPages";

// Admin pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProjects from "@/pages/admin/AdminProjects";
import { AdminNews, AdminAnnouncements, AdminBoard } from "@/pages/admin/AdminContent";
import { AdminMembershipApplications, AdminVolunteerApplications, AdminPartnershipApplications, AdminContactMessages } from "@/pages/admin/AdminApplications";
import { AdminDocuments, AdminSettings } from "@/pages/admin/AdminSettingsDocs";
import AdminPopups from "@/pages/admin/AdminPopups";
import AdminActivityAreas from "@/pages/admin/AdminActivityAreas";
import useSiteSEO from "@/hooks/useSiteSEO";

function withPublicLayout(Component) {
  return (
    <PublicLayout>
      <Component/>
    </PublicLayout>
  );
}

// Wraps PlaceholderPage with fixed props for a specific menu item.
function makePlaceholder(props) {
  return withPublicLayout(() => <PlaceholderPage {...props}/>);
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemeProvider>
            <Toaster position="top-right" richColors closeButton/>
            <AppRoutes/>
          </ThemeProvider>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

function AppRoutes() {
  useSiteSEO();
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);
  return (
    <Routes>
            {/* Public */}
            <Route path="/" element={withPublicLayout(HomePage)}/>
            <Route path="/anasayfa2" element={<Navigate to="/" replace/>}/>

            {/* Neden USEKO */}
            <Route path="/neden-useko/usekonedir" element={makePlaceholder({ overline: "Tanıtım", title: "USEKO Nedir?", description: "USEKO; ekonomik kalkınma, ticari diplomasi ve uluslararası iş birliği alanlarında faaliyet gösteren, Türkiye merkezli uluslararası bir sivil toplum kuruluşudur. Kamu, özel sektör, akademi ve uluslararası kuruluşlar arasında stratejik ortaklıklar geliştirerek sürdürülebilir kalkınmaya ve küresel iş birliğine katkı sunmayı amaçlamaktadır." })}/>
            <Route path="/neden-useko/neden" element={makePlaceholder({ overline: "Yaklaşımımız", title: "Neden USEKO?", description: "Küresel ölçekte sürdürülebilir kalkınma; güçlü ortaklıklar, güvene dayalı iş birlikleri ve ortak akıl gerektirmektedir. USEKO, farklı sektörleri ve ülkeleri ortak hedefler etrafında buluşturarak ekonomik, sosyal ve kültürel değer üreten sürdürülebilir iş birliği modelleri geliştirmektedir." })}/>

            {/* Başkanın Mesajı */}
            <Route path="/baskanin-mesaji" element={withPublicLayout(PresidentMessagePage)}/>

            {/* Kurumsal */}
            <Route path="/kurumsal/hakkimizda" element={withPublicLayout(AboutPage)}/>
            <Route path="/kurumsal/misyon-vizyon" element={withPublicLayout(MissionPage)}/>
            <Route path="/kurumsal/ilkeler" element={withPublicLayout(PrinciplesPage)}/>
            <Route path="/kurumsal/belgeler" element={withPublicLayout(DocumentsPage)}/>
            <Route path="/kurumsal/organizasyon" element={withPublicLayout(OrganizationPage)}/>

            {/* Faaliyet Alanları */}
            <Route path="/faaliyet-alanlari/:slug" element={withPublicLayout(ActivityAreaPage)}/>

            {/* Programlar */}
            <Route path="/programlar/uluslararasi-is-birligi" element={makePlaceholder({ title: "Uluslararası İş Birliği Programları", description: "USEKO; ülkeler, kurumlar ve uluslararası kuruluşlar arasında sürdürülebilir ortaklıklar geliştiren, bilgi paylaşımını ve ortak proje üretimini destekleyen programlar yürütmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/jyh41krp_11-Uluslararas%C4%B1%20%C4%B0%C5%9F%20Birli%C4%9Fi%20Programlar%C4%B1.png" })}/>
            <Route path="/programlar/ticaret-heyetleri" element={makePlaceholder({ title: "Ticaret Heyetleri", description: "USEKO; uluslararası ticaretin geliştirilmesi amacıyla sektörel ticaret heyetleri, iş forumları, yatırım buluşmaları ve B2B görüşmeleri organize etmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/jk6qsu54_12-Ticaret%20Heyetleri.png" })}/>
            <Route path="/programlar/akademik-is-birlikleri" element={makePlaceholder({ title: "Akademik İş Birlikleri", description: "USEKO; üniversiteler, araştırma merkezleri ve düşünce kuruluşları arasında ortak bilimsel çalışmaların ve uluslararası akademik iş birliklerinin geliştirilmesini desteklemektedir." })}/>
            <Route path="/programlar/genclik" element={makePlaceholder({ title: "Gençlik Programları", description: "USEKO; gençlerin liderlik, girişimcilik, uluslararası deneyim ve sosyal sorumluluk alanlarında gelişimini destekleyen programlar yürütmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/1r2ow5l0_13-Gen%C3%A7lik%20Programlar%C4%B1.png" })}/>
            <Route path="/programlar/kadin-girisimciligi" element={makePlaceholder({ title: "Kadın Girişimciliği Programı", description: "USEKO; kadın girişimcilerin ekonomik hayata katılımını güçlendiren, iş geliştirme süreçlerini destekleyen ve uluslararası iş ağlarına erişimlerini artıran programlar geliştirmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/a1lnibht_14-Kad%C4%B1n%20Giri%C5%9Fimcili%C4%9Fi%20Program%C4%B1.png" })}/>
            <Route path="/programlar/gonulluluk" element={makePlaceholder({ title: "Gönüllülük Programı", description: "USEKO; küresel kalkınma, sosyal sorumluluk ve toplumsal fayda projelerinde aktif rol almak isteyen bireyleri bir araya getiren uluslararası bir gönüllülük ağı oluşturmaktadır. Program; farklı coğrafyalarda gerçekleştirilen kalkınma, eğitim, kültür ve sosyal etki projelerine nitelikli insan kaynağı desteği sağlamakta ve küresel vatandaşlık bilincini güçlendirmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/126qyvtj_15-G%C3%B6n%C3%BCll%C3%BCl%C3%BCk%20Program%C4%B1.png" })}/>
            <Route path="/programlar/liderlik" element={makePlaceholder({ title: "Liderlik Programı", description: "USEKO; kamu, özel sektör, akademi ve sivil toplum alanlarında geleceğin liderlerini yetiştirmeyi hedefleyen eğitim, mentorluk ve kapasite geliştirme programları düzenlemektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/5yfdkafq_16-Liderlik%20Program%C4%B1%20.png" })}/>
            <Route path="/programlar/sosyal-etki" element={makePlaceholder({ title: "Sosyal Etki Programları", description: "USEKO; ekonomik ve sosyal kalkınmaya katkı sağlayan, ölçülebilir, sürdürülebilir ve uzun vadeli toplumsal fayda oluşturan projeler geliştirmektedir.", image: "https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/s71d9y9x_17-Sosyal%20Etki%20Programlar%C4%B1.png" })}/>

            {/* Basın Bültenleri */}
            <Route path="/basin-bultenleri" element={makePlaceholder({ overline: "Medya", title: "Basın Bültenleri" })}/>

            {/* Başkanlıklar (legacy, kept for direct links) */}
            <Route path="/baskanliklar/:slug" element={withPublicLayout(ChairmanshipDetailPage)}/>

            {/* Projeler */}
            <Route path="/projeler" element={withPublicLayout(ProjectsListPage)}/>
            <Route path="/projeler/:slug" element={withPublicLayout(ProjectDetailPage)}/>
            <Route path="/suriye-programi" element={withPublicLayout(SyriaProgramPage)}/>

            {/* Başvuru Formları */}
            <Route path="/uyelik" element={withPublicLayout(MembershipPage)}/>
            <Route path="/gonullu-ol" element={withPublicLayout(VolunteerPage)}/>
            <Route path="/is-birligi" element={withPublicLayout(PartnershipPage)}/>

            {/* Haberler / Duyurular */}
            <Route path="/haberler" element={withPublicLayout(NewsListPage)}/>
            <Route path="/haberler/:slug" element={withPublicLayout(NewsDetailPage)}/>
            <Route path="/duyurular" element={withPublicLayout(AnnouncementsListPage)}/>
            <Route path="/duyurular/:slug" element={withPublicLayout(AnnouncementDetailPage)}/>

            {/* İletişim */}
            <Route path="/iletisim" element={withPublicLayout(ContactPage)}/>

            {/* Yasal */}
            <Route path="/kvkk" element={withPublicLayout(KvkkPage)}/>
            <Route path="/gizlilik" element={withPublicLayout(PrivacyPage)}/>
            <Route path="/cerez-politikasi" element={withPublicLayout(CookiesPage)}/>
            <Route path="/kullanim-sartlari" element={withPublicLayout(TermsPage)}/>

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLoginPage/>}/>
            <Route path="/yonetim" element={<AdminLoginPage/>}/>
            <Route path="/admin" element={<ProtectedRoute><AdminLayout/></ProtectedRoute>}>
              <Route index element={<AdminDashboard/>}/>
              <Route path="dashboard" element={<AdminDashboard/>}/>
              <Route path="projects" element={<AdminProjects/>}/>
              <Route path="news" element={<AdminNews/>}/>
              <Route path="announcements" element={<AdminAnnouncements/>}/>
              <Route path="board" element={<AdminBoard/>}/>
              <Route path="popups" element={<AdminPopups/>}/>
              <Route path="activity-areas" element={<AdminActivityAreas/>}/>
              <Route path="applications/membership" element={<AdminMembershipApplications/>}/>
              <Route path="applications/volunteer" element={<AdminVolunteerApplications/>}/>
              <Route path="applications/partnership" element={<AdminPartnershipApplications/>}/>
              <Route path="contact" element={<AdminContactMessages/>}/>
              <Route path="documents" element={<AdminDocuments/>}/>
              <Route path="settings" element={<AdminSettings/>}/>
            </Route>

            {/* 404 */}
            <Route path="*" element={withPublicLayout(NotFoundPage)}/>
    </Routes>
  );
}

export default App;
